import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import portalApi from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';

const EMPTY = {
  insurance_carrier: '', plan_name: '', member_id: '', market_type: '', product_type: '',
  state: '', monthly_premium: '', aptc_amount: '', final_member_responsibility: '',
  effective_date: '', end_date: '', policy_status: 'Active', agent_id: '', notes: '',
};

const REQUIRED = [
  ['insurance_carrier', 'Insurance Carrier'], ['plan_name', 'Plan'], ['policy_status', 'Policy Status'],
  ['effective_date', 'Effective Date'], ['monthly_premium', 'Monthly Premium'],
];

// Currency-safe subtraction using integer cents to avoid floating-point artifacts
// (e.g. 708.08 - 214.00 must be 494.08, not 494.08000000000004).
const subtractCents = (a, b) => (Math.round(a * 100) - Math.round(b * 100)) / 100;

export function RenewCaseDialog({ caseItem, open, onOpenChange, onRenewed }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [agents, setAgents] = useState([]);
  const [memberRespTouched, setMemberRespTouched] = useState(false);
  const [duplicate, setDuplicate] = useState(null); // {existing_case_id, target_year_label}

  const targetYear = useMemo(() => {
    const m = (caseItem?.coverage_year_label || '').match(/\d{4}/);
    return m ? parseInt(m[0], 10) + 1 : null;
  }, [caseItem]);

  useEffect(() => {
    if (!open) return;
    // Pre-fill from the source year's policy (editable), load dropdown data.
    const p = caseItem?.policy || {};
    setForm({
      ...EMPTY,
      insurance_carrier: p.insurance_carrier || '',
      plan_name: p.plan_name || '',
      member_id: p.member_id || '',
      market_type: p.market_type || '',
      product_type: p.product_type || '',
      state: p.state || '',
      monthly_premium: p.monthly_premium != null ? String(p.monthly_premium) : '',
      aptc_amount: p.aptc_amount != null ? String(p.aptc_amount) : '',
      final_member_responsibility: '',
      policy_status: p.policy_status || 'Active',
      agent_id: '',
      notes: '',
    });
    setMemberRespTouched(false);
    setDuplicate(null);
    portalApi.getPolicyCatalog().then(setCatalog).catch(() => setCatalog(null));
    portalApi.getAgents().then(setAgents).catch(() => setAgents([]));
  }, [open, caseItem]);

  // Auto member responsibility = premium - APTC (unless the user typed their own value).
  const autoMemberResp = useMemo(() => {
    const prem = parseFloat(form.monthly_premium);
    const aptc = parseFloat(form.aptc_amount) || 0;
    if (isNaN(prem)) return '';
    return Math.max(0, subtractCents(prem, aptc)).toFixed(2);
  }, [form.monthly_premium, form.aptc_amount]);

  const memberRespValue = memberRespTouched ? form.final_member_responsibility : autoMemberResp;

  const plansForCarrier = useMemo(() => {
    if (!catalog || !form.insurance_carrier) return [];
    return catalog.plans_by_carrier?.[form.insurance_carrier] || [];
  }, [catalog, form.insurance_carrier]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = () => { setForm(EMPTY); setDuplicate(null); onOpenChange(false); };

  const buildPayload = (updateExisting) => ({
    insurance_carrier: form.insurance_carrier.trim() || null,
    plan_name: form.plan_name.trim() || null,
    member_id: form.member_id.trim() || null,
    market_type: form.market_type || null,
    product_type: form.product_type || null,
    state: form.state || null,
    monthly_premium: form.monthly_premium !== '' ? parseFloat(form.monthly_premium) : null,
    aptc_amount: form.aptc_amount !== '' ? parseFloat(form.aptc_amount) : null,
    final_member_responsibility: memberRespValue !== '' ? parseFloat(memberRespValue) : null,
    effective_date: form.effective_date || null,
    end_date: form.end_date || null,
    policy_status: form.policy_status || null,
    agent_id: form.agent_id || null,
    notes: form.notes.trim() || null,
    update_existing: !!updateExisting,
  });

  const submit = async (updateExisting = false) => {
    const missing = REQUIRED.filter(([k]) => !String(form[k] ?? '').trim());
    if (missing.length) {
      toast.error(`Missing required fields: ${missing.map((m) => m[1]).join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const res = await portalApi.renewCase(caseItem.id, buildPayload(updateExisting));
      toast.success(
        res.updated_existing
          ? `Renewal completed for ${res.target_year_label}`
          : `Renewal created for ${res.target_year_label}`,
        { action: { label: 'Open', onClick: () => navigate(`/portal/cases/${res.case.id}`) } },
      );
      close();
      if (onRenewed) onRenewed(res);
    } catch (err) {
      if (err.status === 409 && err.detail?.existing_case_id) {
        setDuplicate(err.detail);
      } else if (err.detail?.missing) {
        toast.error(`Missing required fields: ${err.detail.missing.join(', ')}`);
      } else {
        toast.error(err.message || 'Could not complete the renewal');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="renew-case-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-sky-600" /> Renew for {targetYear || 'the next year'}
          </DialogTitle>
          <DialogDescription>
            Complete the {targetYear || ''} policy for patient <strong>{caseItem?.athena_id || 'no Athena ID'}</strong> in
            one operation. The current case ({caseItem?.coverage_year_label || 'current year'}) is preserved.
          </DialogDescription>
        </DialogHeader>

        {duplicate ? (
          <div className="space-y-4 py-2" data-testid="renew-duplicate-warning">
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                A renewal case already exists for this patient in {duplicate.target_year_label}.
                You can review the existing case or update its policy with the information entered.
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => navigate(`/portal/cases/${duplicate.existing_case_id}`)}
                data-testid="renew-open-existing-btn">
                Open existing case
              </Button>
              <Button onClick={() => submit(true)} disabled={saving} data-testid="renew-update-existing-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Update existing renewal
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600">
                Target Coverage Year: <strong className="text-slate-900">{targetYear || '—'}</strong>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Insurance Carrier <span className="text-red-500">*</span></Label>
                  <Input list="carrier-options" value={form.insurance_carrier}
                    onChange={(e) => set('insurance_carrier', e.target.value)}
                    placeholder="Select or type" data-testid="renew-carrier" />
                  <datalist id="carrier-options">
                    {(catalog?.carriers || []).map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>Plan <span className="text-red-500">*</span></Label>
                  <Input list="plan-options" value={form.plan_name}
                    onChange={(e) => set('plan_name', e.target.value)}
                    placeholder="New year plan" data-testid="renew-plan-name" />
                  <datalist id="plan-options">
                    {plansForCarrier.map((p) => <option key={p} value={p} />)}
                  </datalist>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Market</Label>
                  <Select value={form.market_type} onValueChange={(v) => set('market_type', v)}>
                    <SelectTrigger data-testid="renew-market"><SelectValue placeholder="Market" /></SelectTrigger>
                    <SelectContent>
                      {(catalog?.markets || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select value={form.state} onValueChange={(v) => set('state', v)}>
                    <SelectTrigger data-testid="renew-state"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {(catalog?.states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Coverage Type</Label>
                  <Select value={form.product_type} onValueChange={(v) => set('product_type', v)}>
                    <SelectTrigger data-testid="renew-coverage-type"><SelectValue placeholder="Coverage Type" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {(catalog?.coverage_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Policy Status <span className="text-red-500">*</span></Label>
                  <Select value={form.policy_status} onValueChange={(v) => set('policy_status', v)}>
                    <SelectTrigger data-testid="renew-policy-status"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {(catalog?.policy_statuses || ['Active', 'Pending']).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Monthly Premium ($) <span className="text-red-500">*</span></Label>
                  <Input type="number" step="0.01" value={form.monthly_premium}
                    onChange={(e) => set('monthly_premium', e.target.value)} placeholder="0.00"
                    data-testid="renew-monthly-premium" />
                </div>
                <div className="space-y-2">
                  <Label>APTC ($)</Label>
                  <Input type="number" step="0.01" value={form.aptc_amount}
                    onChange={(e) => set('aptc_amount', e.target.value)} placeholder="0.00"
                    data-testid="renew-aptc-amount" />
                </div>
                <div className="space-y-2">
                  <Label>Member Responsibility ($)</Label>
                  <Input type="number" step="0.01" value={memberRespValue}
                    onChange={(e) => { setMemberRespTouched(true); set('final_member_responsibility', e.target.value); }}
                    placeholder="Auto" data-testid="renew-member-resp" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Effective Date <span className="text-red-500">*</span></Label>
                  <Input type="date" value={form.effective_date}
                    onChange={(e) => set('effective_date', e.target.value)} data-testid="renew-effective-date" />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={form.end_date}
                    onChange={(e) => set('end_date', e.target.value)} data-testid="renew-end-date" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Agent / Producer</Label>
                <Select value={form.agent_id} onValueChange={(v) => set('agent_id', v)}>
                  <SelectTrigger data-testid="renew-agent"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Renewal Notes</Label>
                <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)}
                  placeholder="Optional notes" rows={2} data-testid="renew-notes" />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={saving} data-testid="renew-cancel-btn">
                Cancel
              </Button>
              <Button onClick={() => submit(false)} disabled={saving} data-testid="renew-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Complete Renewal
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
